import { useState, useEffect, useCallback } from 'react';
import { useOnboarding } from '../OnboardingContext';
import {
  AlertCircle, Check, Shield, Building2,
  Mail, MessageSquare, Phone, ChevronDown, ChevronUp,
  Send, Loader2, BadgeCheck, Clock, Fingerprint, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { FlagImg } from '@/components/ui/CountryFlag';
import { SumsubVerification, type ExtractedData } from '../SumsubVerification';
import { VerificationConfirmScreen, type ConfirmedPersonData } from '../VerificationConfirmScreen';
import { calculateEffectiveOwnership } from '@/utils/effectiveOwnership';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';


import { formatLabel } from '@/lib/format-labels';
/* ─── Types ────────────────────────────────────────────────────── */
interface KycPerson {
  id: string;
  name: string;
  role: string;
  type: 'director' | 'ubo' | 'authorized';
  effectiveOwnership?: number;
  ownershipPath?: string[];
  email: string;
  phone: string;
  completed: boolean;
  linkSentVia?: 'email' | 'sms' | 'whatsapp';
  linkSentAt?: string;
  // UI state
  sendPanelOpen: 'email' | 'sms' | 'whatsapp' | null;
  saving: boolean;
  sending: 'email' | 'sms' | 'whatsapp' | null;
  // Confirmed KYC fields from VerificationConfirmScreen
  confirmedData?: ConfirmedPersonData;
}

/* ─── Phone country codes ───────────────────────────────────────── */
const PHONE_CODES = [
  { code: '+44', flag: '🇬🇧', country: 'United Kingdom', iso2: 'GB' },
  { code: '+1',  flag: '🇺🇸', country: 'United States', iso2: 'US' },
  { code: '+33', flag: '🇫🇷', country: 'France', iso2: 'FR' },
  { code: '+49', flag: '🇩🇪', country: 'Germany', iso2: 'DE' },
  { code: '+34', flag: '🇪🇸', country: 'Spain', iso2: 'ES' },
  { code: '+39', flag: '🇮🇹', country: 'Italy', iso2: 'IT' },
  { code: '+31', flag: '🇳🇱', country: 'Netherlands', iso2: 'NL' },
  { code: '+32', flag: '🇧🇪', country: 'Belgium', iso2: 'BE' },
  { code: '+41', flag: '🇨🇭', country: 'Switzerland', iso2: 'CH' },
  { code: '+351', flag: '🇵🇹', country: 'Portugal', iso2: 'PT' },
  { code: '+353', flag: '🇮🇪', country: 'Ireland', iso2: 'IE' },
  { code: '+45', flag: '🇩🇰', country: 'Denmark', iso2: 'DK' },
  { code: '+46', flag: '🇸🇪', country: 'Sweden', iso2: 'SE' },
  { code: '+47', flag: '🇳🇴', country: 'Norway', iso2: 'NO' },
  { code: '+358', flag: '🇫🇮', country: 'Finland', iso2: 'FI' },
  { code: '+48', flag: '🇵🇱', country: 'Poland', iso2: 'PL' },
  { code: '+43', flag: '🇦🇹', country: 'Austria', iso2: 'AT' },
  { code: '+30', flag: '🇬🇷', country: 'Greece', iso2: 'GR' },
  { code: '+351', flag: '🇵🇹', country: 'Portugal', iso2: 'PT' },
  { code: '+420', flag: '🇨🇿', country: 'Czech Republic', iso2: 'CZ' },
  { code: '+36', flag: '🇭🇺', country: 'Hungary', iso2: 'HU' },
  { code: '+40', flag: '🇷🇴', country: 'Romania', iso2: 'RO' },
  { code: '+971', flag: '🇦🇪', country: 'UAE', iso2: 'AE' },
  { code: '+966', flag: '🇸🇦', country: 'Saudi Arabia', iso2: 'SA' },
  { code: '+91',  flag: '🇮🇳', country: 'India', iso2: 'IN' },
  { code: '+86',  flag: '🇨🇳', country: 'China', iso2: 'CN' },
  { code: '+81',  flag: '🇯🇵', country: 'Japan', iso2: 'JP' },
  { code: '+55',  flag: '🇧🇷', country: 'Brazil', iso2: 'BR' },
  { code: '+27',  flag: '🇿🇦', country: 'South Africa', iso2: 'ZA' },
  { code: '+61',  flag: '🇦🇺', country: 'Australia', iso2: 'AU' },
  { code: '+65',  flag: '🇸🇬', country: 'Singapore', iso2: 'SG' },
];

/* ─── PhoneInput ────────────────────────────────────────────────── */
function PhoneInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [countryCode, setCountryCode] = useState('+44');
  const [number, setNumber] = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!value) return;
    const matched = PHONE_CODES.find(c => value.startsWith(c.code));
    if (matched) { setCountryCode(matched.code); setNumber(value.slice(matched.code.length).trim()); }
    else setNumber(value);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = (code: string, num: string) => onChange(`${code}${num}`);
  const filtered = search.trim() ? PHONE_CODES.filter(c => c.country.toLowerCase().includes(search.toLowerCase()) || c.code.includes(search)) : [];
  const selected = PHONE_CODES.find(c => c.code === countryCode) || PHONE_CODES[0];

  return (
    <div className="space-y-1 w-full">
      <div className="flex gap-2">
        <button type="button" onClick={() => { setOpen(v => !v); setSearch(''); }}
          className="flex items-center gap-1.5 px-3 h-10 rounded-xl border border-input bg-background hover:bg-muted/40 transition-colors text-sm whitespace-nowrap shrink-0">
          <FlagImg code={selected.iso2} size="sm" />
          <span className="font-medium text-xs">{selected.code}</span>
          <ChevronDown className={cn('w-3 h-3 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </button>
        <Input type="tel" value={number}
          onChange={e => { const n = e.target.value.replace(/\D/g, '').slice(0, 15); setNumber(n); emit(countryCode, n); }}
          placeholder="Mobile number" className="h-10 text-sm flex-1" />
      </div>
      {open && (
        <div className="rounded-xl border border-border bg-popover overflow-hidden w-60 shadow-lg z-10">
          <input type="text" placeholder="Search country..." value={search} autoFocus
            onChange={e => setSearch(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-muted/40 border-b border-border focus:outline-none placeholder:text-muted-foreground" style={{ fontSize: '16px' }} />
          <div className="max-h-40 overflow-y-auto">
            {search.trim() === '' ? (
              <p className="px-3 py-2.5 text-xs text-muted-foreground text-center">Start typing to search…</p>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-2.5 text-xs text-muted-foreground text-center">No results</p>
            ) : filtered.slice(0, 25).map((c, i) => (
              <button key={`${c.code}-${i}`} type="button"
                onClick={() => { setCountryCode(c.code); emit(c.code, number); setOpen(false); setSearch(''); }}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/60 transition-colors text-left text-xs border-b border-border/30 last:border-0">
                <FlagImg code={c.iso2} size="xs" />
                <span className="flex-1 truncate text-foreground">{c.country}</span>
                <span className="text-muted-foreground font-medium shrink-0">{c.code}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────── */
export function CorporateBiometricsStep() {
  const { data, updateData, goToNextStep, setCustomBackHandler } = useOnboarding();
  const company = data.corporateCompany;

  const [persons, setPersons] = useState<KycPerson[]>([]);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<{ id: string; extractedData?: ExtractedData; fromQueue?: boolean } | null>(null);
  const [fetchingConfirmFor, setFetchingConfirmFor] = useState<string | null>(null); // personId being fetched before confirm screen
  // Queue of person IDs that need confirm screen shown (remote-link verified, no confirmedData yet)
  const [confirmQueue, setConfirmQueue] = useState<string[]>([]);

  /* ── Build persons list ─────────────────────────────────────────── */
  useEffect(() => {
    if (!company) return;
    const list: KycPerson[] = [];
    const seen = new Map<string, number>(); // key -> index in list
    const HONORIFICS = /^(mr\.?|mrs\.?|ms\.?|miss|dr\.?|prof\.?|sir|lord|dame)\s+/i;
    const norm = (n: string) => n.toLowerCase().trim().replace(HONORIFICS, '').trim();
    const applicantEmail = data.email || '';
    const applicantPhone = data.phoneNumber ? `${data.countryCode || ''}${data.phoneNumber}` : '';
    const existing = data.corporateBiometrics || [];
    const findEx = (id: string) => existing.find(b => b.id === id);

    // Helper: add person or merge role into existing entry
    const addOrMerge = (key: string, person: KycPerson) => {
      if (seen.has(key)) {
        const idx = seen.get(key)!;
        const existing = list[idx];
        // Merge role labels (e.g. "Director · Authorised Person")
        if (!existing.role.includes(person.role)) {
          existing.role = `${existing.role} · ${person.role}`;
        }
        // Fill in missing email/phone from the new entry
        if (!existing.email && person.email) existing.email = person.email;
        if (!existing.phone && person.phone) existing.phone = person.phone;
        return;
      }
      seen.set(key, list.length);
      list.push(person);
    };

    // Directors
    for (const d of company.directors || []) {
      const key = norm(d.name);
      const id = `director-${key.replace(/\s+/g, '-')}`;
      const ex = findEx(id);
      addOrMerge(key, { id, name: d.name, role: 'Director', type: 'director',
        email: ex?.email || applicantEmail, phone: ex?.phone || applicantPhone,
        completed: ex?.completed || false, linkSentVia: ex?.linkSentVia, linkSentAt: ex?.linkSentAt,
        confirmedData: (ex as any)?.confirmedData,
        sendPanelOpen: null, saving: false, sending: null });
    }

    // UBOs ≥25%
    if (company.shareholders?.length) {
      const owners = calculateEffectiveOwnership(company.shareholders);
      for (const o of owners.filter(x => x.requiresBiometrics)) {
        const key = norm(o.name);
        const id = `ubo-${key.replace(/\s+/g, '-')}`;
        const ex = findEx(id);
        addOrMerge(key, { id, name: o.name, role: `${o.effectiveOwnership.toFixed(1)}% Shareholder`, type: 'ubo',
          effectiveOwnership: o.effectiveOwnership, ownershipPath: o.path,
          email: ex?.email || '', phone: ex?.phone || '',
          completed: ex?.completed || false, linkSentVia: ex?.linkSentVia, linkSentAt: ex?.linkSentAt,
          confirmedData: (ex as any)?.confirmedData,
          sendPanelOpen: null, saving: false, sending: null });
      }
    }

    // Authorized person
    if (company.authorizedPerson) {
      const { firstName, lastName, email, phoneNumber, phoneCountryCode } = company.authorizedPerson;
      const fullName = `${firstName} ${lastName}`.trim();
      const key = norm(fullName);
      const id = 'authorized-person';
      const ex = findEx(id);
      const authContact = (company as any).authorizedPersonContact;
      const pEmail = email || authContact?.email || '';
      const pPhone = phoneNumber ? `${phoneCountryCode || ''}${phoneNumber}` : authContact?.phoneNumber ? `${authContact.phoneCountryCode || ''}${authContact.phoneNumber}` : '';
      addOrMerge(key, { id, name: fullName, role: 'Authorised Person', type: 'authorized',
        email: ex?.email || pEmail, phone: ex?.phone || pPhone,
        completed: ex?.completed || false, linkSentVia: ex?.linkSentVia, linkSentAt: ex?.linkSentAt,
        confirmedData: (ex as any)?.confirmedData,
        sendPanelOpen: null, saving: false, sending: null });
    }

    setPersons(list);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  /* ── Back handler ───────────────────────────────────────────────── */
  const handleBack = useCallback(() => {
    if (verifyingId) { setVerifyingId(null); return true; }
    return false;
  }, [verifyingId]);
  useEffect(() => {
    setCustomBackHandler(handleBack);
    return () => setCustomBackHandler(null);
  }, [handleBack, setCustomBackHandler]);

  /* ── Sync to context ────────────────────────────────────────────── */
  const syncToContext = (updated: KycPerson[]) => {
    updateData({
      corporateBiometrics: updated.map(p => ({
        id: p.id, name: p.name,
        type: p.type === 'authorized' ? 'authorized' : p.type === 'director' ? 'director' : 'shareholder',
        shareholding: p.effectiveOwnership, completed: p.completed,
        email: p.email, phone: p.phone, linkSentVia: p.linkSentVia, linkSentAt: p.linkSentAt,
        // Confirmed KYC fields from the review screen
        ...(p.confirmedData || {}),
      })),
    });
  };

  /* ── Update a field on a person ─────────────────────────────────── */
  const updateField = (id: string, field: 'email' | 'phone', value: string) => {
    setPersons(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  /* ── Toggle send panel ──────────────────────────────────────────── */
  const togglePanel = (id: string, via: 'email' | 'sms' | 'whatsapp') => {
    setPersons(prev => prev.map(p =>
      p.id === id
        ? { ...p, sendPanelOpen: p.sendPanelOpen === via ? null : via }
        : { ...p, sendPanelOpen: null }
    ));
  };

  /* ── Send link ──────────────────────────────────────────────────── */
  const sendLink = async (person: KycPerson, via: 'email' | 'sms' | 'whatsapp') => {
    setPersons(prev => prev.map(p => p.id === person.id ? { ...p, sending: via } : p));

    // Always use the published domain for SMS/WhatsApp links — preview URLs are filtered by carriers
    const baseUrl = 'https://ionboarding.lovable.app';
    const verificationUrl = `${baseUrl}/verify?person=${encodeURIComponent(person.id)}`;
    const firstName = person.name.split(' ')[0];
    const message = `Hi ${firstName}, please complete your identity verification for ${company?.name || 'our application'}: ${verificationUrl}`;

    try {
      if (via === 'email' && person.email) {
        await supabase.functions.invoke('send-sumsub-link', {
          body: { email: person.email, name: person.name, verification_url: verificationUrl, session_id: (data as any).sessionId }
        }).catch(() => {/* silent */});
      } else if (via === 'sms' && person.phone) {
        const { error: smsError } = await supabase.functions.invoke('send-verification-sms', {
          body: { phoneNumber: person.phone, name: person.name, message }
        });
        if (smsError) throw smsError;
      } else if (via === 'whatsapp' && person.phone) {
        const clean = person.phone.replace(/[^\d]/g, '');
        window.open(`https://wa.me/${clean}?text=${encodeURIComponent(message)}`, '_blank');
      }

      const sentAt = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const updated = persons.map(p =>
        p.id === person.id ? { ...p, sending: null, linkSentVia: via, linkSentAt: sentAt, sendPanelOpen: null } : p
      );
      setPersons(updated);
      syncToContext(updated);
    } catch {
      setPersons(prev => prev.map(p => p.id === person.id ? { ...p, sending: null } : p));
    }
  };

  /* ── Save contact ───────────────────────────────────────────────── */
  const saveContact = async (person: KycPerson) => {
    setPersons(prev => prev.map(p => p.id === person.id ? { ...p, saving: true } : p));
    try {
      const sessionId = (data as any).sessionId || (data as any).session_id;
      if (sessionId) {
        await supabase.from('application_notes').insert({
          session_id: sessionId, admin_user_id: 'system',
          note_text: `Contact updated for ${person.name} — email: ${person.email}, phone: ${person.phone}`,
          is_internal: true,
        }).then(() => {});
      }
      const updated = persons.map(p => p.id === person.id ? { ...p, saving: false } : p);
      setPersons(updated);
      syncToContext(updated);
    } catch {
      setPersons(prev => prev.map(p => p.id === person.id ? { ...p, saving: false } : p));
    }
  };

  /* ── Verification complete → show confirm screen ────────────────── */
  const handleVerificationComplete = (extractedData?: ExtractedData) => {
    // Don't mark complete yet — show confirm screen first
    setPendingConfirm({ id: verifyingId!, extractedData });
    setVerifyingId(null);
  };

  /* ── User confirmed on the confirm screen — save all 16 fields ──── */
  const handleConfirmComplete = (confirmed: ConfirmedPersonData) => {
    const targetId = pendingConfirm?.id;
    const fromQueue = pendingConfirm?.fromQueue ?? false;
    if (!targetId) return;
    const updated = persons.map(p =>
      p.id === targetId
        ? { ...p, completed: true, sendPanelOpen: null, confirmedData: confirmed,
            email: confirmed.email || p.email, phone: confirmed.phone || p.phone }
        : p
    );
    setPersons(updated);
    syncToContext(updated);
    setPendingConfirm(null);

    if (fromQueue) {
      // Process next in queue (remote-link verified persons who still need confirm screen)
      setConfirmQueue(prev => {
        const next = prev.filter(id => id !== targetId);
        if (next.length > 0) {
          fetchAndShowConfirm(next[0], updated, true);
        } else {
          goToNextStep();
        }
        return next;
      });
    }
    // If not from queue (inline Sumsub), just return to overview — user still clicks Continue
  };

  /* ── Fetch Sumsub data for a remote-verified person & show confirm ── */
  const fetchAndShowConfirm = async (personId: string, currentPersons: KycPerson[], fromQueue = false) => {
    setFetchingConfirmFor(personId);
    const person = currentPersons.find(p => p.id === personId);
    if (!person) { setFetchingConfirmFor(null); return; }

    try {
      const sessionId = (data as any).sessionId || (data as any).session_id;

      // Per-person sumsubApplicantId (stored when inline verification completes)
      // OR fall back to session-level sumsubApplicantId (works for single-person flows)
      const perPersonApplicantId = (person as any).sumsubApplicantId as string | undefined;
      const sessionApplicantId = (data as any).sumsubApplicantId as string | undefined;
      const applicantId = perPersonApplicantId || (currentPersons.length === 1 ? sessionApplicantId : undefined);

      // session-level externalUserId (what Sumsub actually registered the applicant under)
      const sessionExternalUserId = (data as any).sumsubExternalUserId as string | undefined;

      const { data: result } = await supabase.functions.invoke('fetch-sumsub-data', {
        body: {
          // Direct applicantId is the most reliable lookup — try it first
          ...(applicantId ? { applicantId } : {}),
          // externalUserId fallbacks: person.id (used for remote links), then session-level
          externalUserId: personId,
          // Pass session externalUserId as additional fallback hint
          ...(sessionExternalUserId && sessionExternalUserId !== personId ? { fallbackExternalUserId: sessionExternalUserId } : {}),
          email: person.email || data.email || undefined,
          sessionId,
          personName: person.name,
          forceRefresh: true,
        }
      });

      const ext = result?.extracted;
      const extractedData: ExtractedData | undefined = ext ? {
        firstName: ext.firstName,
        lastName: ext.lastName,
        dateOfBirth: ext.dateOfBirth,
        address: ext.address,
        document: {
          documentType: ext.documentType,
          documentNumber: ext.documentNumber,
          documentExpiry: ext.documentExpiry,
          documentIssueDate: ext.documentIssueDate,
          documentCountry: ext.documentCountry,
          nationality: ext.nationality,
        },
        poa: ext.poaVerified ? { address: ext.poaAddress || ext.address, documentType: ext.poaDocumentType } : undefined,
      } : undefined;

      setPendingConfirm({ id: personId, extractedData, fromQueue });
    } catch (e) {
      console.error('Failed to fetch Sumsub data for confirm screen:', e);
      // Show confirm screen anyway (blank, user fills manually)
      setPendingConfirm({ id: personId, extractedData: undefined, fromQueue });
    } finally {
      setFetchingConfirmFor(null);
    }
  };

  /* ── Guards ─────────────────────────────────────────────────────── */
  if (!company) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 pt-24">
      <div className="text-center"><AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" /><p className="text-muted-foreground">Company data not found.</p></div>
    </div>
  );

  if (persons.length === 0) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 pt-24">
      <div className="text-center"><Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4 animate-pulse" /><p className="text-muted-foreground">Preparing verification list…</p></div>
    </div>
  );

  /* ── Confirm screen (after Sumsub, before marking complete) ─────── */
  if (pendingConfirm) {
    const confirmPerson = persons.find(p => p.id === pendingConfirm.id);
    const nameParts = (confirmPerson?.name || '').trim().split(/\s+/);
    return (
      <VerificationConfirmScreen
        personName={confirmPerson?.name || ''}
        knownFirstName={nameParts[0]}
        knownLastName={nameParts.slice(1).join(' ')}
        personRole={confirmPerson?.role}
        email={confirmPerson?.email || data.email || ''}
        phone={confirmPerson?.phone || `${data.countryCode || ''}${data.phoneNumber || ''}`}
        extractedData={pendingConfirm.extractedData}
        onConfirm={handleConfirmComplete}
      />
    );
  }

  /* ── Inline Sumsub verification view ────────────────────────────── */
  if (verifyingId) {
    const person = persons.find(p => p.id === verifyingId)!;
    return (
      <div className="onboarding-container animate-slide-in">
        <div className="flex-1 flex flex-col p-6 max-w-md mx-auto w-full">
          <div className="pt-16 pb-6">
            <Badge variant="secondary" className="mb-4">{formatLabel(person.role)}</Badge>
            <h1 className="text-2xl font-bold text-foreground mb-1">Verify {person.name}</h1>
            <p className="text-muted-foreground text-sm">Complete identity verification to continue.</p>
          </div>
          <SumsubVerification
            onComplete={handleVerificationComplete}
            onError={() => setVerifyingId(null)}
            email={person.email || data.email || undefined}
            phone={person.phone || `${data.countryCode}${data.phoneNumber}`}
            includePOA={true}
            personName={person.name}
            personId={person.id}
          />
          <div className="flex-1" />
        </div>
      </div>
    );
  }

  /* ── Overview ───────────────────────────────────────────────────── */
  const completedCount = persons.filter(p => p.completed).length;
  const allCompleted = completedCount === persons.length;

  return (
    <div className="onboarding-container animate-slide-in">
      <div className="flex-1 flex flex-col p-6 max-w-lg mx-auto w-full">

        {/* Header */}
        <div className="pt-16 pb-6">
          <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center mb-5">
            <Shield className="w-7 h-7 text-accent" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Identity Verification</h1>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Tap a person to verify them here on this device, or use the icons to send them a verification link via email, SMS, or WhatsApp.
          </p>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground text-xs">Verification progress</span>
            <span className="text-xs font-semibold text-foreground">{completedCount} / {persons.length} verified</span>
          </div>
          <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
            <div className="h-full bg-accent transition-all duration-500 ease-out" style={{ width: `${(completedCount / persons.length) * 100}%` }} />
          </div>
        </div>

        {/* Person cards */}
        <div className="space-y-3 mb-8">
          {persons.map(person => {
            const hasSent = !!person.linkSentVia;

            return (
              <div key={person.id} className={cn(
                'rounded-2xl border transition-all duration-200',
                person.completed
                  ? 'bg-success/5 border-success/30'
                  : person.sendPanelOpen
                    ? 'bg-card border-accent/40 shadow-md shadow-accent/10'
                    : 'bg-card border-border'
              )}>

                {/* ── Main row ── */}
                <div className="flex items-center gap-3 p-4">
                  {/* Avatar / status indicator */}
                  <div className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold',
                    person.completed ? 'bg-success/20 text-success' : 'bg-accent/10 text-accent'
                  )}>
                    {person.completed
                      ? <BadgeCheck className="w-5 h-5" />
                      : person.name.split(' ').map(w => w[0]).slice(0, 2).join('')
                    }
                  </div>

                  {/* Name + role — clicking starts Sumsub directly */}
                  <button
                    type="button"
                    disabled={person.completed}
                    onClick={() => !person.completed && setVerifyingId(person.id)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <p className="font-semibold text-foreground text-sm truncate">{person.name}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {person.role.split(' · ').map((r, ri) => (
                          <Badge key={ri} variant="outline" className="text-[10px] px-1.5 py-0">{r}</Badge>
                        ))}
                      </div>
                      {hasSent && !person.completed && (
                        <span className="flex items-center gap-1 text-xs text-warning">
                          <Clock className="w-3 h-3" />
                          Link sent · awaiting
                        </span>
                      )}
                    </div>
                  </button>

                  {/* Right-side actions */}
                  {person.completed ? (
                    <Badge className="bg-success/15 text-success border-0 text-xs shrink-0">✓ Verified</Badge>
                  ) : (
                    <div className="flex items-center gap-1 shrink-0">
                      {/* Verify on device */}
                      <button
                        type="button"
                        title="Verify on this device"
                        disabled={person.completed}
                        onClick={() => !person.completed && setVerifyingId(person.id)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-accent hover:bg-accent/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Fingerprint className="w-4 h-4" />
                      </button>
                      {/* Email */}
                      <button
                        type="button"
                        title="Send link by email"
                        onClick={() => togglePanel(person.id, 'email')}
                        className={cn(
                          'w-8 h-8 rounded-lg flex items-center justify-center transition-colors',
                          person.sendPanelOpen === 'email'
                            ? 'bg-accent/15 text-accent'
                            : person.linkSentVia === 'email'
                              ? 'text-success'
                              : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                        )}
                      >
                        {person.linkSentVia === 'email'
                          ? <Check className="w-4 h-4" />
                          : <Mail className="w-4 h-4" />
                        }
                      </button>
                      {/* SMS */}
                      <button
                        type="button"
                        title="Send link by SMS"
                        onClick={() => togglePanel(person.id, 'sms')}
                        className={cn(
                          'w-8 h-8 rounded-lg flex items-center justify-center transition-colors',
                          person.sendPanelOpen === 'sms'
                            ? 'bg-accent/15 text-accent'
                            : person.linkSentVia === 'sms'
                              ? 'text-success'
                              : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                        )}
                      >
                        {person.linkSentVia === 'sms'
                          ? <Check className="w-4 h-4" />
                          : <Phone className="w-4 h-4" />
                        }
                      </button>
                      {/* WhatsApp */}
                      <button
                        type="button"
                        title="Send link via WhatsApp"
                        onClick={() => togglePanel(person.id, 'whatsapp')}
                        className={cn(
                          'w-8 h-8 rounded-lg flex items-center justify-center transition-colors',
                          person.sendPanelOpen === 'whatsapp'
                            ? 'bg-accent/15 text-accent'
                            : person.linkSentVia === 'whatsapp'
                              ? 'text-success'
                              : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                        )}
                      >
                        {person.linkSentVia === 'whatsapp'
                          ? <Check className="w-4 h-4" />
                          : <MessageSquare className="w-4 h-4" />
                        }
                      </button>
                    </div>
                  )}
                </div>

                {/* ── Send panel (email / sms / whatsapp) ── */}
                {person.sendPanelOpen && !person.completed && (
                  <div className="px-4 pb-4 pt-0 border-t border-border/40">
                    <div className="pt-3 space-y-3">
                      {/* Email panel */}
                      {person.sendPanelOpen === 'email' && (
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                            <Mail className="w-3 h-3" /> Send verification link by email
                          </label>
                          <div className="flex gap-2">
                            <Input
                              type="email"
                              value={person.email}
                              onChange={e => updateField(person.id, 'email', e.target.value)}
                              placeholder="email@example.com"
                              className="h-10 text-sm flex-1"
                              autoFocus
                            />
                            <Button
                              type="button"
                              size="sm"
                              disabled={!person.email || person.sending === 'email'}
                              onClick={() => { saveContact(person); sendLink(person, 'email'); }}
                              className="h-10 px-4 gap-1.5 shrink-0 text-xs"
                            >
                              {person.sending === 'email'
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <Send className="w-3.5 h-3.5" />
                              }
                              Send
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* SMS panel */}
                      {person.sendPanelOpen === 'sms' && (
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                            <Phone className="w-3 h-3" /> Send verification link by SMS
                          </label>
                          <PhoneInput value={person.phone} onChange={val => updateField(person.id, 'phone', val)} />
                          <Button
                            type="button"
                            size="sm"
                            disabled={!person.phone || person.sending === 'sms'}
                            onClick={() => { saveContact(person); sendLink(person, 'sms'); }}
                            className="w-full h-10 gap-2 text-xs"
                          >
                            {person.sending === 'sms'
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Send className="w-3.5 h-3.5" />
                            }
                            Send SMS link
                          </Button>
                        </div>
                      )}

                      {/* WhatsApp panel */}
                      {person.sendPanelOpen === 'whatsapp' && (
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                            <MessageSquare className="w-3 h-3" /> Send verification link via WhatsApp
                          </label>
                          <PhoneInput value={person.phone} onChange={val => updateField(person.id, 'phone', val)} />
                          <Button
                            type="button"
                            size="sm"
                            disabled={!person.phone || person.sending === 'whatsapp'}
                            onClick={() => { saveContact(person); sendLink(person, 'whatsapp'); }}
                            className="w-full h-10 gap-2 text-xs bg-[hsl(142,70%,32%)] hover:bg-[hsl(142,70%,28%)] text-white border-0"
                          >
                            {person.sending === 'whatsapp'
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <MessageSquare className="w-3.5 h-3.5" />
                            }
                            Send WhatsApp link
                          </Button>
                        </div>
                      )}

                      {/* Sent confirmation */}
                      {person.linkSentVia && (
                        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-success/8 border border-success/25">
                          <Check className="w-3.5 h-3.5 text-success flex-shrink-0" />
                          <p className="text-xs text-success">
                            Link sent via {person.linkSentVia} at {person.linkSentAt}. Awaiting completion.
                          </p>
                        </div>
                      )}

                      {/* Close panel */}
                      <button
                        type="button"
                        onClick={() => togglePanel(person.id, person.sendPanelOpen!)}
                        className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-1 flex items-center justify-center gap-1"
                      >
                        <X className="w-3 h-3" /> Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="p-4 bg-muted/30 border border-border/50 rounded-xl mb-6">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="flex flex-col items-center gap-1">
              <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center">
                <Fingerprint className="w-3.5 h-3.5 text-accent" />
              </div>
              <span className="text-[10px] text-muted-foreground">Verify here</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="w-7 h-7 rounded-lg bg-muted/60 flex items-center justify-center">
                <Mail className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
              <span className="text-[10px] text-muted-foreground">Send by email</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="w-7 h-7 rounded-lg bg-muted/60 flex items-center justify-center">
                <Phone className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
              <span className="text-[10px] text-muted-foreground">Send by SMS/WA</span>
            </div>
          </div>
        </div>

        <div className="flex-1" />

        <Button
          onClick={() => {
            if (!allCompleted) return;
            // Find persons who verified via remote link (completed=true) but have no confirmedData yet
            const needsConfirm = persons.filter(p => p.completed && !p.confirmedData);
            if (needsConfirm.length === 0) {
              goToNextStep();
              return;
            }
            // Build queue and start with the first one
            const queue = needsConfirm.map(p => p.id);
            setConfirmQueue(queue.slice(1));
            fetchAndShowConfirm(queue[0], persons, true);
          }}
          disabled={!allCompleted || !!fetchingConfirmFor}
          className={cn(
            'w-full py-6 text-base font-semibold mb-8 transition-all',
            (!allCompleted || !!fetchingConfirmFor) && 'opacity-50 cursor-not-allowed'
          )}
        >
          {fetchingConfirmFor
            ? 'Loading verification details…'
            : allCompleted
              ? 'Continue to next step →'
              : `${persons.length - completedCount} verification${persons.length - completedCount !== 1 ? 's' : ''} remaining — cannot continue`}
        </Button>
      </div>
    </div>
  );
}
